import type { CSSProperties, ReactNode } from 'react';
import { COMPANY, registeredDetailsLine } from '../lib/company';

type Props = {
  /** Rendered as the last item in the link row when the user is signed in. */
  onSignOut?: () => void;
  /** Build string shown alongside the copyright line. */
  version?: string;
};

const linkStyle: CSSProperties = {
  color: 'rgba(14,14,14,.62)',
  textDecoration: 'none',
  whiteSpace: 'nowrap',
};

/** External links all carry noopener + noreferrer, matching TermsLine. */
function Out({ href, children }: { href: string; children: ReactNode }) {
  return (
    <a href={href} target="_blank" rel="noopener noreferrer" style={linkStyle}>
      {children}
    </a>
  );
}

/**
 * The public footer. Shown both signed-out (login page) and signed-in (Hub),
 * because the signed-out version is the only one an OAuth reviewer, a
 * reputation service or a client's IT team will ever see.
 *
 * Carries the statutory trading disclosures required by s.82 Companies Act
 * 2006, plus the outbound links that tie this domain to the established
 * Jones + Palmer identity. See src/lib/company.ts.
 */
export function SiteFooter({ onSignOut, version }: Props) {
  return (
    <footer
      style={{
        borderTop: '1px solid rgba(14,14,14,.10)',
        paddingTop: 22,
        display: 'flex',
        flexDirection: 'column',
        gap: 14,
        fontSize: 13,
      }}
    >
      <nav
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '10px 18px',
          alignItems: 'center',
        }}
      >
        <Out href={COMPANY.url}>jonesandpalmer.co.uk</Out>
        <Out href={COMPANY.contactUrl}>Contact</Out>
        <a href="/terms" target="_blank" rel="noopener noreferrer" style={linkStyle}>
          Terms
        </a>
        <a href="/privacy" target="_blank" rel="noopener noreferrer" style={linkStyle}>
          Privacy
        </a>
        <Out href={COMPANY.linkedInUrl}>LinkedIn</Out>
        <Out href={COMPANY.instagramUrl}>Instagram</Out>
        <Out href={COMPANY.companiesHouseUrl}>Companies House</Out>
        {onSignOut && (
          <button
            onClick={onSignOut}
            style={{
              ...linkStyle,
              background: 'none',
              border: 0,
              padding: 0,
              fontSize: 13,
              cursor: 'pointer',
            }}
          >
            Sign out
          </button>
        )}
      </nav>

      <address
        style={{
          fontStyle: 'normal',
          color: 'rgba(14,14,14,.52)',
          fontSize: 12,
          lineHeight: 1.6,
          maxWidth: 760,
        }}
      >
        {registeredDetailsLine()}
        <br />
        ICO registration {COMPANY.icoRegistration} ·{' '}
        <a href={COMPANY.phoneHref} style={{ color: 'inherit' }}>
          {COMPANY.phone}
        </a>{' '}
        ·{' '}
        <a href={`mailto:${COMPANY.email}`} style={{ color: 'inherit' }}>
          {COMPANY.email}
        </a>
      </address>

      <div
        className="mono"
        style={{
          color: 'rgba(14,14,14,.45)',
          display: 'flex',
          gap: 14,
          flexWrap: 'wrap',
        }}
      >
        <span>
          © {COMPANY.legalName} {new Date().getFullYear()}
        </span>
        {version && <span>{version}</span>}
      </div>
    </footer>
  );
}
